import React, { useEffect, useMemo, useState } from "react";
import { trackEvent } from "../analytics";

const questions = [
  "Do you know whether your company is directly or indirectly affected by CSRD?",
  "Have you identified internal ESG data owners?",
  "Have you completed a double materiality assessment?",
  "Do you track Scope 1 and Scope 2 emissions?",
  "Do you have a plan for Scope 3 data collection?",
  "Do you document ESG policies, controls, and evidence?",
  "Are sustainability risks reviewed by leadership or the board?",
  "Do you map disclosures against ESRS requirements?",
  "Do you have a process for reviewing AI-assisted ESG drafts?",
  "Can you produce investor-ready ESG reporting outputs?"
];

function getReadinessLevel(score) {
  if (score >= 75) return "Strong readiness";
  if (score >= 45) return "Developing readiness";
  return "Early-stage readiness";
}

function getRecommendation(score) {
  if (score >= 75) {
    return "Your organization has a solid CSRD foundation. The next step is improving evidence quality, workflow automation, and investor-ready disclosure outputs.";
  }

  if (score >= 45) {
    return "Your organization has started preparing, but likely still has gaps in materiality, ESRS mapping, evidence ownership, or reporting workflows.";
  }

  return "Your organization should begin with CSRD scoping, ESG data ownership, double materiality, and a basic disclosure gap assessment.";
}

function getPriorityGaps(score) {
  if (score >= 75) {
    return [
      "Strengthen evidence quality and audit trail documentation",
      "Improve workflow automation across ESG data owners",
      "Prepare board-ready and investor-ready disclosure packs"
    ];
  }

  if (score >= 45) {
    return [
      "Complete or update the double materiality assessment",
      "Map disclosures against ESRS requirements",
      "Assign clear owners for ESG data, controls, and evidence"
    ];
  }

  return [
    "Confirm whether the organization is directly or indirectly affected by CSRD",
    "Identify ESG data owners across finance, HR, operations, and governance",
    "Start a basic CSRD and ESRS disclosure gap assessment"
  ];
}

function getStrengths(answers) {
  const strengths = [];

  if (answers[1] >= 5) strengths.push("Some ESG data ownership is already in place");
  if (answers[2] >= 5) strengths.push("Double materiality preparation has started");
  if (answers[3] >= 5) strengths.push("Scope 1 and Scope 2 emissions are being considered");
  if (answers[5] >= 5) strengths.push("ESG policies, controls, or evidence are being documented");
  if (answers[9] >= 5) strengths.push("The organization is thinking about investor-ready reporting outputs");

  if (strengths.length === 0) {
    strengths.push("The organization has started the right first step by assessing CSRD readiness");
  }

  return strengths;
}

function getActionPlan(score) {
  if (score >= 75) {
    return {
      thirtyDays: [
        "Review current evidence quality and identify weak documentation areas",
        "Map existing ESG controls to CSRD and ESRS reporting needs",
        "Define owners for final disclosure review and sign-off"
      ],
      sixtyDays: [
        "Automate recurring ESG data collection workflows",
        "Prepare board-ready reporting summaries",
        "Create a repeatable reporting calendar for future cycles"
      ],
      ninetyDays: [
        "Move toward assurance-ready evidence management",
        "Benchmark performance against sector peers",
        "Prepare investor-grade sustainability reporting outputs"
      ]
    };
  }

  if (score >= 45) {
    return {
      thirtyDays: [
        "Confirm CSRD exposure and reporting timeline",
        "Assign ESG data owners across functions",
        "Start ESRS gap mapping for priority topics"
      ],
      sixtyDays: [
        "Complete or refresh the double materiality assessment",
        "Document available evidence and missing disclosures",
        "Create a remediation plan with owners and deadlines"
      ],
      ninetyDays: [
        "Draft reviewable ESG disclosure narratives",
        "Prepare leadership reporting packs",
        "Introduce a structured workflow for review, approval, and export"
      ]
    };
  }

  return {
    thirtyDays: [
      "Confirm whether CSRD affects the organization directly or indirectly",
      "Identify internal stakeholders across finance, HR, operations, legal, and governance",
      "Create a baseline ESG data inventory"
    ],
    sixtyDays: [
      "Run a first double materiality screening",
      "Identify missing policies, metrics, and evidence",
      "Prioritize gaps by risk, effort, and reporting deadline"
    ],
    ninetyDays: [
      "Build a practical CSRD readiness roadmap",
      "Start collecting evidence for priority ESRS topics",
      "Prepare an initial management-ready readiness summary"
    ]
  };
}

export default function CSRDReadinessAssessment() {
  const [answers, setAnswers] = useState({});
  const [leadForm, setLeadForm] = useState({
    fullName: "",
    email: "",
    companyName: "",
    role: ""
  });
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadError, setLeadError] = useState("");

  useEffect(() => {
    trackEvent("resource_view", {
      resource: "csrd_readiness_assessment",
      path: window.location.pathname
    });
  }, []);

  const answeredCount = Object.keys(answers).length;
  const isAssessmentComplete = answeredCount === questions.length;

  const score = useMemo(() => {
    const total = questions.length * 10;
    const points = Object.values(answers).reduce(
      (sum, value) => sum + Number(value || 0),
      0
    );

    return Math.round((points / total) * 100);
  }, [answers]);

  const level = getReadinessLevel(score);
  const recommendation = getRecommendation(score);
  const priorityGaps = getPriorityGaps(score);
  const strengths = getStrengths(answers);
  const actionPlan = getActionPlan(score);

  const setAnswer = (index, value) => {
    setAnswers((prev) => {
      const nextAnswers = { ...prev, [index]: value };

      if (Object.keys(prev).length === 0) {
        trackEvent("assessment_start", {
          assessment: "csrd_readiness",
          path: window.location.pathname
        });
      }

      return nextAnswers;
    });
  };

  const handleLeadChange = (field, value) => {
    setLeadForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const submitLeadCapture = (event) => {
    event.preventDefault();

    const normalizedEmail = String(leadForm.email || "").trim().toLowerCase();

    if (!leadForm.fullName.trim()) {
      setLeadError("Please enter your name.");
      return;
    }

    if (!normalizedEmail || !normalizedEmail.includes("@")) {
      setLeadError("Please enter a valid work email.");
      return;
    }

    if (!leadForm.companyName.trim()) {
      setLeadError("Please enter your company name.");
      return;
    }

    setLeadError("");
    setLeadCaptured(true);

    trackEvent("assessment_lead_captured", {
      assessment: "csrd_readiness",
      fullName: leadForm.fullName.trim(),
      email: normalizedEmail,
      companyName: leadForm.companyName.trim(),
      role: leadForm.role.trim(),
      score,
      readinessLevel: level,
      path: window.location.pathname
    });

    trackEvent("assessment_complete", {
      assessment: "csrd_readiness",
      score,
      readinessLevel: level,
      companyName: leadForm.companyName.trim(),
      role: leadForm.role.trim(),
      path: window.location.pathname
    });
  };

  const bookDemo = () => {
    trackEvent("demo_click", {
      source: "csrd_readiness_assessment",
      score,
      readinessLevel: level,
      companyName: leadForm.companyName || "",
      path: window.location.pathname
    });

    window.location.href =
      "mailto:hello@onedigitale.com?subject=CSRD Readiness Assessment Demo Request";
  };

  const createAccount = () => {
    trackEvent("assessment_create_account_click", {
      source: "csrd_readiness_assessment",
      score,
      readinessLevel: level,
      companyName: leadForm.companyName || "",
      path: window.location.pathname
    });

    window.location.href = `/?signup=true&email=${encodeURIComponent(
      leadForm.email || ""
    )}`;
  };

  return (
    <main style={pageStyle}>
      <section style={heroStyle}>
        <a href="/" style={backLinkStyle}>
          ← Back to Ecovanta
        </a>

        <div style={eyebrowStyle}>CSRD Readiness Assessment</div>

        <h1 style={titleStyle}>Check your CSRD readiness in 3 minutes</h1>

        <p style={introStyle}>
          Answer 10 practical questions to estimate your organization&apos;s
          readiness for CSRD and ESRS reporting. This assessment helps identify
          whether your ESG reporting foundation is early-stage, developing, or
          strong.
        </p>

        <div style={heroNoteStyle}>
          This assessment is available without payment. Ecovanta platform access
          starts with the paid Starter plan.
        </div>
      </section>

      <section style={contentStyle}>
        <div style={assessmentStyle}>
          {questions.map((question, index) => (
            <div key={question} style={questionStyle}>
              <h3 style={questionTitleStyle}>
                {index + 1}. {question}
              </h3>

              <div style={optionRowStyle}>
                <button
                  style={
                    answers[index] === 0
                      ? selectedButtonStyle
                      : optionButtonStyle
                  }
                  onClick={() => setAnswer(index, 0)}
                >
                  No
                </button>

                <button
                  style={
                    answers[index] === 5
                      ? selectedButtonStyle
                      : optionButtonStyle
                  }
                  onClick={() => setAnswer(index, 5)}
                >
                  Partly
                </button>

                <button
                  style={
                    answers[index] === 10
                      ? selectedButtonStyle
                      : optionButtonStyle
                  }
                  onClick={() => setAnswer(index, 10)}
                >
                  Yes
                </button>
              </div>
            </div>
          ))}
        </div>

        <aside style={scoreCardStyle}>
          <div style={scoreLabelStyle}>Your progress</div>
          <div style={scoreStyle}>{score}/100</div>

          <h2>{level}</h2>

          <p style={{ lineHeight: 1.7 }}>{recommendation}</p>

          <div style={progressBarTrackStyle}>
            <div
              style={{
                ...progressBarFillStyle,
                width: `${answeredCount * 10}%`
              }}
            />
          </div>

          <div style={progressTextStyle}>
            {answeredCount}/{questions.length} questions answered
          </div>

          {!isAssessmentComplete && (
            <div style={scoreHintStyle}>
              Complete all 10 questions to unlock your readiness report and
              action plan.
            </div>
          )}

          {isAssessmentComplete && !leadCaptured && (
            <form style={leadFormStyle} onSubmit={submitLeadCapture}>
              <h3 style={leadFormTitleStyle}>Unlock your readiness report</h3>

              <p style={leadFormTextStyle}>
                Enter your details to view your detailed score, key gaps, and
                recommended 90-day CSRD readiness action plan.
              </p>

              <input
                style={inputStyle}
                placeholder="Full name"
                value={leadForm.fullName}
                onChange={(event) =>
                  handleLeadChange("fullName", event.target.value)
                }
              />

              <input
                style={inputStyle}
                placeholder="Work email"
                value={leadForm.email}
                onChange={(event) =>
                  handleLeadChange("email", event.target.value)
                }
              />

              <input
                style={inputStyle}
                placeholder="Company name"
                value={leadForm.companyName}
                onChange={(event) =>
                  handleLeadChange("companyName", event.target.value)
                }
              />

              <input
                style={inputStyle}
                placeholder="Role / job title optional"
                value={leadForm.role}
                onChange={(event) =>
                  handleLeadChange("role", event.target.value)
                }
              />

              {leadError && <div style={leadErrorStyle}>{leadError}</div>}

              <button type="submit" style={primaryButtonStyle}>
                View My Readiness Report
              </button>
            </form>
          )}

          {isAssessmentComplete && leadCaptured && (
            <>
              <button style={primaryButtonStyle} onClick={createAccount}>
                Create Account to Continue
              </button>

              <button style={secondaryActionButtonStyle} onClick={bookDemo}>
                Book a CSRD Readiness Demo
              </button>

              <a href="/guides/csrd-reporting" style={guideLinkStyle}>
                Read the CSRD Guide →
              </a>
            </>
          )}
        </aside>
      </section>

      {isAssessmentComplete && leadCaptured && (
        <section style={reportSectionStyle}>
          <div style={reportCardStyle}>
            <div style={reportHeaderStyle}>
              <div>
                <div style={eyebrowStyle}>Your Readiness Report</div>
                <h2 style={reportTitleStyle}>{level}</h2>
              </div>

              <div style={reportScoreBadgeStyle}>{score}/100</div>
            </div>

            <p style={reportIntroStyle}>{recommendation}</p>

            <div style={reportGridStyle}>
              <ReportPanel title="Strengths">
                {strengths.map((item) => (
                  <CheckItem key={item} text={item} />
                ))}
              </ReportPanel>

              <ReportPanel title="Priority gaps">
                {priorityGaps.map((item) => (
                  <CheckItem key={item} text={item} />
                ))}
              </ReportPanel>
            </div>

            <div style={actionPlanStyle}>
              <h3 style={actionPlanTitleStyle}>
                Recommended 90-day CSRD action plan
              </h3>

              <div style={actionPlanGridStyle}>
                <ActionColumn title="Next 30 days" items={actionPlan.thirtyDays} />
                <ActionColumn title="Next 60 days" items={actionPlan.sixtyDays} />
                <ActionColumn title="Next 90 days" items={actionPlan.ninetyDays} />
              </div>
            </div>

            <div style={reportCtaStyle}>
              <div>
                <h3 style={{ margin: "0 0 8px 0" }}>
                  Turn this score into a structured ESG reporting workflow
                </h3>
                <p style={{ margin: 0, color: "#cbd5e1", lineHeight: 1.7 }}>
                  Ecovanta helps teams move from readiness assessment to
                  structured ESRS-aligned reporting, AI-assisted drafting, gap
                  analysis, and investor-ready outputs.
                </p>
              </div>

              <button style={reportCtaButtonStyle} onClick={createAccount}>
                Start with the paid Starter plan
              </button>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

function ReportPanel({ title, children }) {
  return (
    <div style={reportPanelStyle}>
      <h3 style={reportPanelTitleStyle}>{title}</h3>
      <div style={{ display: "grid", gap: "10px" }}>{children}</div>
    </div>
  );
}

function CheckItem({ text }) {
  return (
    <div style={checkItemStyle}>
      <span style={checkIconStyle}>✓</span>
      <span>{text}</span>
    </div>
  );
}

function ActionColumn({ title, items }) {
  return (
    <div style={actionColumnStyle}>
      <h4 style={actionColumnTitleStyle}>{title}</h4>
      <div style={{ display: "grid", gap: "10px" }}>
        {items.map((item) => (
          <div key={item} style={actionItemStyle}>
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

const pageStyle = {
  fontFamily:
    'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  background: "#f8fafc",
  color: "#0f172a",
  minHeight: "100vh"
};

const heroStyle = {
  maxWidth: "1120px",
  margin: "0 auto",
  padding: "56px 24px 24px"
};

const backLinkStyle = {
  color: "#475569",
  textDecoration: "none",
  fontWeight: 700
};

const eyebrowStyle = {
  marginTop: "32px",
  color: "#047857",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontSize: "13px"
};

const titleStyle = {
  fontSize: "clamp(36px, 5vw, 64px)",
  lineHeight: 1.02,
  margin: "16px 0",
  letterSpacing: "-0.04em"
};

const introStyle = {
  fontSize: "20px",
  lineHeight: 1.7,
  color: "#475569",
  maxWidth: "880px"
};

const heroNoteStyle = {
  marginTop: "18px",
  color: "#64748b",
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "14px",
  padding: "12px 14px",
  maxWidth: "760px",
  fontSize: "14px",
  lineHeight: 1.6
};

const contentStyle = {
  maxWidth: "1120px",
  margin: "0 auto",
  padding: "24px",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 360px",
  gap: "28px"
};

const assessmentStyle = {
  display: "grid",
  gap: "16px"
};

const questionStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "20px",
  padding: "20px",
  boxShadow: "0 10px 30px rgba(15,23,42,0.05)"
};

const questionTitleStyle = {
  marginTop: 0,
  lineHeight: 1.45
};

const optionRowStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap"
};

const optionButtonStyle = {
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  borderRadius: "12px",
  padding: "10px 16px",
  fontWeight: 800,
  cursor: "pointer"
};

const selectedButtonStyle = {
  ...optionButtonStyle,
  background: "#0f172a",
  color: "#ffffff"
};

const scoreCardStyle = {
  position: "sticky",
  top: "88px",
  alignSelf: "start",
  background: "#0f172a",
  color: "#ffffff",
  borderRadius: "24px",
  padding: "26px",
  boxShadow: "0 18px 48px rgba(15,23,42,0.2)"
};

const scoreLabelStyle = {
  color: "#a7f3d0",
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  fontSize: "12px"
};

const scoreStyle = {
  fontSize: "56px",
  fontWeight: 900,
  margin: "8px 0 12px"
};

const progressBarTrackStyle = {
  width: "100%",
  height: "10px",
  borderRadius: "999px",
  background: "rgba(255,255,255,0.18)",
  overflow: "hidden",
  marginTop: "18px"
};

const progressBarFillStyle = {
  height: "100%",
  borderRadius: "999px",
  background: "#10b981"
};

const progressTextStyle = {
  color: "#cbd5e1",
  fontSize: "13px",
  marginTop: "8px"
};

const scoreHintStyle = {
  marginTop: "18px",
  color: "#cbd5e1",
  lineHeight: 1.6,
  fontSize: "14px"
};

const leadFormStyle = {
  marginTop: "20px",
  display: "grid",
  gap: "10px"
};

const leadFormTitleStyle = {
  margin: "0 0 4px 0",
  color: "#ffffff"
};

const leadFormTextStyle = {
  margin: "0 0 8px 0",
  color: "#cbd5e1",
  lineHeight: 1.6,
  fontSize: "14px"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid rgba(255,255,255,0.24)",
  background: "rgba(255,255,255,0.08)",
  color: "#ffffff",
  borderRadius: "12px",
  padding: "12px 13px",
  fontWeight: 700,
  outline: "none"
};

const leadErrorStyle = {
  color: "#fecaca",
  fontSize: "13px",
  fontWeight: 800
};

const primaryButtonStyle = {
  border: "none",
  background: "#ffffff",
  color: "#0f172a",
  borderRadius: "14px",
  padding: "14px 18px",
  fontWeight: 900,
  cursor: "pointer",
  width: "100%",
  marginTop: "12px"
};

const secondaryActionButtonStyle = {
  ...primaryButtonStyle,
  background: "transparent",
  color: "#ffffff",
  border: "1px solid rgba(255,255,255,0.34)"
};

const guideLinkStyle = {
  display: "block",
  marginTop: "18px",
  color: "#a7f3d0",
  textDecoration: "none",
  fontWeight: 900
};

const reportSectionStyle = {
  maxWidth: "1120px",
  margin: "0 auto",
  padding: "24px 24px 72px"
};

const reportCardStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "26px",
  padding: "32px",
  boxShadow: "0 18px 50px rgba(15,23,42,0.08)"
};

const reportHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "20px",
  alignItems: "center",
  flexWrap: "wrap"
};

const reportTitleStyle = {
  fontSize: "42px",
  margin: "10px 0 0 0",
  letterSpacing: "-1px"
};

const reportScoreBadgeStyle = {
  background: "#ecfdf5",
  color: "#047857",
  border: "1px solid #a7f3d0",
  borderRadius: "20px",
  padding: "18px 22px",
  fontSize: "32px",
  fontWeight: 900
};

const reportIntroStyle = {
  color: "#475569",
  fontSize: "18px",
  lineHeight: 1.75,
  maxWidth: "900px"
};

const reportGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "18px",
  marginTop: "24px"
};

const reportPanelStyle = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "20px",
  padding: "22px"
};

const reportPanelTitleStyle = {
  margin: "0 0 14px 0"
};

const checkItemStyle = {
  display: "flex",
  gap: "10px",
  lineHeight: 1.55,
  color: "#334155"
};

const checkIconStyle = {
  color: "#10b981",
  fontWeight: 900
};

const actionPlanStyle = {
  marginTop: "28px"
};

const actionPlanTitleStyle = {
  fontSize: "26px",
  marginBottom: "16px"
};

const actionPlanGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: "16px"
};

const actionColumnStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "18px",
  padding: "18px"
};

const actionColumnTitleStyle = {
  margin: "0 0 12px 0",
  color: "#047857"
};

const actionItemStyle = {
  color: "#334155",
  lineHeight: 1.55,
  background: "#f8fafc",
  borderRadius: "12px",
  padding: "10px 12px"
};

const reportCtaStyle = {
  marginTop: "30px",
  background: "#0f172a",
  color: "#ffffff",
  borderRadius: "22px",
  padding: "24px",
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto",
  gap: "18px",
  alignItems: "center"
};

const reportCtaButtonStyle = {
  border: "none",
  background: "#ffffff",
  color: "#0f172a",
  borderRadius: "14px",
  padding: "14px 18px",
  fontWeight: 900,
  cursor: "pointer",
  whiteSpace: "nowrap"
};